import { xor } from "lodash";
import { useState } from "react";
import {
  Box,
  Button,
  MenuList,
  Popover,
  Stack,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid-pro";

import { Icon } from "@/components/Icon";
import { useGenericAddPatientModal } from "@/modals/genericForm";
import { css, Theme } from "@/theme";
import { initialState, setActiveView, setFilters, usePatientList } from "./patient-slice";
import { Text } from "@imminently/react/ui/core/text";
import { usePopover } from "@/components/ContextMenu";
import { EncounterStatus } from "@/resources/types";
import { useAppDispatch } from "@/store";
import { useAdvancedFiltersModal } from "./advancedFilterModal";
import { useAddExpectsPatientModal } from "@/modals/addExpectsPatient";
import { SplitButton } from "@/components/SplitButton";
import { patientListColumns } from "@/components/PatientList/PatientListColumns";
import { usePatientListApiRefCtx } from "@/components/PatientList/PatientListAPIRefCtx";
import FilterOption from "@/components/FilterOption";
import { getAreaOptions } from "@/resources/locationHelpers";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";
import { PATIENT_LIST_VIEWS } from "./patient-views";

const styles = {
  alert: (theme: Theme) => css`
    display: inline-flex;
    border-radius: 0.75rem;
    color: ${theme.palette.error.dark};
    background-color: ${theme.palette.error.light};
    min-height: 1.75rem;
    min-width: 2.25rem;
    padding: 0 0.75rem;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    line-height: 1.2;
  `,
  clear: css`
    .MuiButton-startIcon {
      margin: 0;
    }
  `,
};

// TODO pause, this feature needs further discussion and is delayed till june
// const PauseButton = () => {
//   const [isPaused, setIsPaused] = useState(false);
//   const togglePause = () => {
//     // TODO need to get the connection and pause it some how
//     // this means the connection interface needs to be a singleton or in a context
//     // ideally the code to connect could be in a component, a context can then made which implements the connection
//     // context and provider could probably be generic and used for any connection
//     setIsPaused(!isPaused);
//   };
//   return (
//     <Button color={isPaused ? "warning" : "primary"} variant="outlined" sx={{ gap: 1 }} onClick={togglePause}>
//       {isPaused ? (<><PlayIcon />Resume</>) : (<><PauseIcon />Pause</>)}
//     </Button>
//   );
// };

// TODO: move this to a translation file
const getStatusFilterOptionName = (type: EncounterStatus) => {
  switch (type) {
    case "disabled":
      return "Archived";
    case "pendingDischarged":
      return "Pending Discharge";
    case "didnotpresent":
      return "Did not attend";
    default:
      return type;
  }
};

const QuickFilter = () => {
  const dispatch = useAppDispatch();
  const { onClick, onClose, ...popoverProps } = usePopover("quick-filter");
  const { filters, activeView } = usePatientList();
  const { settings } = useSettingsContext();
  const locations = ((settings as any)?.locations ?? []) as any[];
  const areaOptions = getAreaOptions(locations);

  const [area, setArea] = useState<string[]>(filters.area ?? []);
  const [status, setStatus] = useState<string[]>(filters.status ?? []);

  const toggleArea = (value: string) => () => {
    setArea(xor(area, [value]));
  };

  const toggleStatus = (value: string) => () => {
    const index = status.indexOf(value);
    if (index === -1) {
      setStatus([...status, value].sort());
    } else {
      setStatus(status.filter((v) => v !== value));
    }
  };

  const handleClose = () => {
    if (JSON.stringify(filters) !== JSON.stringify({ area, status })) {
      // Save to redux
      dispatch(setFilters({ area, status }));
    }

    onClose();
  };

  const resetFilters = () => {
    setArea(initialState.filters.area);
    setStatus(initialState.filters.status);
  };

  const statusChecked = (value: string) => status.indexOf(value) !== -1;
  const numFilters = area.length + status.length;
  const isViewActive = !!activeView;

  const button = (
    <Button
      onClick={onClick}
      variant="outlined"
      endIcon={<Icon icon="chevrondown" />}
      disableFocusRipple
      disabled={isViewActive}
    >
      <Stack direction="row" gap={1} alignItems="center">
        Quick filters
        {numFilters > 0 && <Box css={styles.alert}>{numFilters}</Box>}
      </Stack>
    </Button>
  );

  return (
    <>
      <Tooltip
        title={
          isViewActive
            ? "Clear the active view to use quick filters."
            : ""
        }
        disableHoverListener={!isViewActive}
      >
        <span>{button}</span>
      </Tooltip>
      <Popover onClose={handleClose} {...popoverProps}>
        <MenuList sx={{ width: numFilters > 0 ? 182 : 150, py: 2 }}>
          <Text sx={{ px: 2, fontSize: "0.75rem" }} variant="h6">
            Area
          </Text>
          <Stack pt={1} gap={0.5}>
            {areaOptions.map((areaOption, index) => (
              <FilterOption
                key={index}
                name={areaOption.label}
                checked={area.includes(areaOption.value)}
                onClick={toggleArea(areaOption.value)}
              />
            ))}
          </Stack>

          <Text sx={{ px: 2, pt: 2, fontSize: "0.75rem" }} variant="h6">
            Status
          </Text>
          <Stack pt={1} gap={0.5}>
            {EncounterStatus.map((value) => (
              <FilterOption
                key={value}
                name={getStatusFilterOptionName(value)}
                checked={statusChecked(value)}
                onClick={toggleStatus(value)}
              />
            ))}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" px={2} pt={2}>
            <Button
              css={styles.clear}
              variant="text"
              size="small"
              startIcon={<Icon height={16} icon="retry" />}
              onClick={resetFilters}
            >
              Reset filters
            </Button>
          </Stack>
        </MenuList>
      </Popover>
    </>
  );
};

const Views = () => {
  const dispatch = useAppDispatch();
  const { activeView } = usePatientList();
  const { onClick, onClose, ...popoverProps } = usePopover("patient-views");

  const handleSelect = (name: string) => () => {
    const next = activeView === name ? null : name;
    dispatch(setActiveView(next));
    onClose();
  };

  const handleClearActiveView = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    dispatch(setActiveView(null));
  };

  return (
    <>
      <Button
        onClick={onClick}
        variant="outlined"
        endIcon={<Icon icon="chevrondown" />}
        disableFocusRipple
      >
        <Stack direction="row" gap={1} alignItems="center">
          Views
          {activeView && (
            <Box
              css={styles.alert}
              sx={{ cursor: "pointer", gap: 0.5 }}
              onClick={handleClearActiveView}
            >
              <span>{activeView}</span>
              <Icon icon="close" height={12} />
            </Box>
          )}
        </Stack>
      </Button>
      <Popover onClose={onClose} {...popoverProps}>
        <MenuList sx={{ py: 1 }}>
          {PATIENT_LIST_VIEWS.map((view) => {
            const selected = activeView === view.name;
            return (
              <ListItemButton
                key={view.name}
                onClick={handleSelect(view.name)}
                selected={selected}
                sx={{ px: 2 }}
              >
                <ListItemText
                  primary={view.name}
                  primaryTypographyProps={{
                    variant: "caption",
                    fontWeight: selected ? 700 : 400,
                  }}
                  sx={{ m: 0 }}
                />
              </ListItemButton>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
};

const Columns = () => {
  const { state, toggleColumnVisibility } = usePatientListApiRefCtx();
  const { onClick, onClose, ...popoverProps } = usePopover(
    "patient-list-columns",
    { matchButtonWidth: true }
  );

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <Button
        onClick={onClick}
        variant="outlined"
        startIcon={<Icon icon="tablesettings" />}
        disableFocusRipple
      >
        Manage columns
      </Button>
      <Popover onClose={handleClose} {...popoverProps}>
        <MenuList>
          <Stack gap={0.5}>
            {state.type !== "empty" &&
              state.localStorageValue.orderedColNames.map((colName) => {
                const colDef = patientListColumns.find(
                  (it) => it.field === colName
                );

                if (colDef === undefined) return null;

                return (
                  <FilterOption
                    key={colName}
                    name={colDef.headerName || ""}
                    checked={state.localStorageValue.columnsVisibility[colName]}
                    onClick={() => toggleColumnVisibility(colName)}
                  />
                );
              })}
          </Stack>
        </MenuList>
      </Popover>
    </>
  );
};

export const PatientToolbar = () => {
  const openAddPatientModal = useGenericAddPatientModal();
  const openAddExpectsPatientModal = useAddExpectsPatientModal();
  const openAdvancedFiltersModal = useAdvancedFiltersModal();
  const { advancedFilters, activeView } = usePatientList();
  const apiRef = useGridApiContext();
  const { isConciergeMode } = useConciergeMode();

  const exportCsv = () => {
    apiRef.current.exportDataAsCsv();
  };

  const openConciergeModal = () => {
    openAddPatientModal({ configId: "concierge" });
  };

  const numFilters = activeView
    ? 0
    : Object.values(advancedFilters).filter((v) => v).length;
  const isViewActive = !!activeView;

  return (
    <Stack direction="row" gap={2} px={3} py={2}>
      <QuickFilter />
      <Tooltip
        title={
          isViewActive
            ? "Clear the active view to use advanced filters."
            : ""
        }
        disableHoverListener={!isViewActive}
      >
        <span>
          <Button
            variant="outlined"
            startIcon={<Icon icon="build" />}
            onClick={openAdvancedFiltersModal}
            disabled={isViewActive}
          >
            <Stack direction="row" gap={1}>
              Advanced filters
              {numFilters > 0 && <Box css={styles.alert}>{numFilters}</Box>}
            </Stack>
          </Button>
        </span>
      </Tooltip>
      <Views />
      <Box flex={1}></Box>
      <Columns />
      <Button variant="outlined" sx={{ gap: 1 }} onClick={exportCsv}>
        <Icon icon="save" />
        Export
      </Button>
      {isConciergeMode ? (
        <Button
          variant="contained"
          color="success"
          onClick={openConciergeModal}
        >
          Create new patient
        </Button>
      ) : (
        <>
          <Button variant="outlined" startIcon={<Icon icon="calendartime" />} onClick={() => openAddExpectsPatientModal({})}>
            Create expects
          </Button>
          <SplitButton
            color="success"
            hideSelectedIndex
            matchButtonWidth
            options={[
              { label: "Create new patient", onClick: openAddPatientModal },
              { label: "Create concierge", onClick: openConciergeModal },
            ]}
          />
        </>
      )}
    </Stack>
  );
};

export default PatientToolbar;
